import type { Empresa } from "@/componentes/empresa/tipos"
import { MarcaBlanca, Titulillo } from "@/componentes/impresos/piezas"
import type { Persona } from "@/componentes/personas/persona"

import { type Fila, normasDe } from "./contenido"
import { Filas } from "./Filas"
import { FondoCarnet } from "./FondoCarnet"
import { CARNET, MARGEN } from "./medidas"
import { cuerpoEnUnaLinea } from "./tipografia"

/**
 * El reverso: a quién llamar si le pasa algo, las normas del carnet y la firma que lo
 * autoriza.
 *
 * ⚠ **El reverso existe por el contacto de emergencia, no por simetría.** Es la única
 * razón operativa por la que una agencia imprime carnets en vez de una lista de
 * nombres: si alguien se accidenta en el muelle, el dato tiene que estar encima de la
 * persona. Por eso va lo primero, y el teléfono es lo más grande de la cara.
 *
 * ⚠ **NO lleva membrete.** `EncabezadoEmpresa` es el único sitio que lo compone (ver
 * `CLAUDE.md`). Lo que sí se leen son campos SUELTOS de `Empresa`, por sus accesores:
 * el teléfono dentro de la última norma y el representante legal bajo la firma. Un
 * dato no es un membrete; tres puestos en bloque, sí.
 *
 * ⚠ **La firma es una LÍNEA, no un campo.** Se firma a mano después de imprimir y antes
 * de plastificar; una rúbrica impresa no autoriza nada.
 *
 * ⚠ **Se imprime como una pieza APARTE, al lado de la cara.** No hay dúplex que
 * cuadrar: salen las dos en la misma hoja, se recortan y se pegan espalda contra
 * espalda antes de plastificar. Una impresora de oficina no registra las dos caras
 * con la precisión que pide una tarjeta de 54 mm, y un reverso desplazado dos
 * milímetros arruina la tarjeta entera — que ya está plastificada cuando se ve.
 */
export function CarnetReverso({ persona, empresa }: { persona: Persona; empresa?: Empresa }) {
  const { ancho, alto } = CARNET
  // Sin contacto no se pinta un guion ni un hueco reservado: se pinta lo que hay. La
  // pantalla avisa de que la ficha está incompleta antes de imprimir.
  const emergencia: Fila[] = [
    { etiqueta: "Avisar a", valor: (persona.contacto_emergencia ?? "").trim() },
    { etiqueta: "Teléfono", valor: (persona.telefono_emergencia ?? "").trim(), realce: true },
  ].filter((f) => f.valor !== "")
  const firmante = (empresa?.representante_legal ?? "").trim()

  return (
    <article
      className="pieza-impresa"
      style={{
        position: "relative",
        width: `${ancho}mm`,
        height: `${alto}mm`,
        fontFamily: "var(--font-carnet)",
      }}
      aria-label={`Reverso del carnet de ${persona.nombres} ${persona.apellidos}`}
    >
      <FondoCarnet cara="reverso" />

      {/* Arriba a la derecha, donde la ola es honda: como en el diseño pedido. */}
      <div style={{ position: "absolute", top: "3.6mm", right: "5mm" }}>
        <MarcaBlanca alto={7.5} />
      </div>

      {/* El relleno de arriba salta la ola de la cabecera y el de abajo, la del pie. */}
      <div
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: `25.5mm ${MARGEN + 2}mm 12mm`,
          color: "var(--impreso-tinta)",
        }}
      >
        <Titulillo ancho={12} tamano={2.3}>
          En caso de emergencia
        </Titulillo>
        <Filas filas={emergencia} ancho={ancho - (MARGEN + 2) * 2} style={{ marginTop: "1.8mm" }} />

        <ul
          style={{
            listStyle: "none",
            margin: "3.2mm 0 0",
            padding: 0,
            display: "grid",
            gap: "0.9mm",
          }}
        >
          {normasDe(empresa).map((norma) => (
            <li
              key={norma}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "1.4mm",
                fontSize: "1.95mm",
                lineHeight: 1.25,
              }}
            >
              <span
                aria-hidden={true}
                style={{
                  flexShrink: 0,
                  width: "1.1mm",
                  height: "1.1mm",
                  borderRadius: "50%",
                  background: "var(--impreso-vino)",
                }}
              />
              <span>{norma}</span>
            </li>
          ))}
        </ul>

        {/* 26 mm y no más: por la izquierda la línea se acerca a la ola del pie. */}
        <div style={{ marginTop: "auto", alignSelf: "center", width: "26mm", textAlign: "center" }}>
          <div style={{ borderTop: "0.25mm solid var(--impreso-gris)" }} />
          <p
            style={{
              margin: "0.8mm 0 0",
              fontSize: "1.8mm",
              letterSpacing: "0.25mm",
              textTransform: "uppercase",
              color: "var(--impreso-gris)",
            }}
          >
            Firma autorizada
          </p>
          {firmante ? (
            <p
              style={{
                margin: "0.3mm 0 0",
                fontSize: `${cuerpoEnUnaLinea(firmante, 30, { tope: 2.4, minimo: 1.8, espaciado: 0.1 })}mm`,
                fontWeight: 700,
                letterSpacing: "0.1mm",
                textTransform: "uppercase",
              }}
            >
              {firmante}
            </p>
          ) : null}
        </div>
      </div>
    </article>
  )
}
